// -------------------------------
// ESIC CONFIGURATION INTERFACES
// -------------------------------
export interface PendingEsicConfiguration {
    esic_code?: string;
    office_name?: string;
    office_address?: string;
    employer_contribution_rate?: number;
    employee_contribution_rate?: number;
    wage_limit?: number;
    effective_from?: string;
    effective_to?: string;
    status?: "active" | "inactive";
}

export interface PendingDeleteEsicConfiguration {
    awaitingCode?: boolean;
}

// -------------------------------
// CREATE ESIC CONFIGURATION — START
// -------------------------------
export const startCreateEsicConfigurationFlow = (
    setPending: (data: PendingEsicConfiguration | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    esicCodeFromIntent?: string
) => {
    if (esicCodeFromIntent) {
        setPending({ esic_code: esicCodeFromIntent });

        pushAIChat("What is the office name?");
        speak("What is the office name?");
        return;
    }

    setPending({});
    pushAIChat("Sure! What is the ESIC code?");
    speak("What is the ESIC code?");
};

// -------------------------------
// CREATE ESIC CONFIGURATION — PROCESS
// -------------------------------
export const processPendingEsicConfiguration = (
    text: string,
    pending: PendingEsicConfiguration,
    setPending: (data: PendingEsicConfiguration | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — ESIC CODE
    if (!pending.esic_code) {
        const esic_code = text.trim();

        if (!esic_code) {
            pushAIChat("Please provide a valid ESIC code.");
            speak("Please provide a valid ESIC code.");
            return;
        }

        setPending({ ...pending, esic_code });
        pushAIChat("What is the office name?");
        speak("What is the office name?");
        return;
    }

    // STEP 2 — OFFICE NAME
    if (!pending.office_name) {
        setPending({ ...pending, office_name: text.trim() });
        pushAIChat("What is the office address?");
        speak("What is the office address?");
        return;
    }

    // STEP 3 — OFFICE ADDRESS
    if (!pending.office_address) {
        setPending({ ...pending, office_address: text.trim() });
        pushAIChat("Employer contribution rate (%)?");
        speak("Employer contribution rate percentage?");
        return;
    }

    // STEP 4 — EMPLOYER RATE
    if (pending.employer_contribution_rate === undefined) {
        const rate = Number(text);
        if (isNaN(rate)) {
            pushAIChat("Please provide a valid employer contribution rate.");
            speak("Please provide a valid employer contribution rate.");
            return;
        }

        setPending({ ...pending, employer_contribution_rate: rate });
        pushAIChat("Employee contribution rate (%)?");
        speak("Employee contribution rate percentage?");
        return;
    }

    // STEP 5 — EMPLOYEE RATE
    if (pending.employee_contribution_rate === undefined) {
        const rate = Number(text);
        if (isNaN(rate)) {
            pushAIChat("Please provide a valid employee contribution rate.");
            speak("Please provide a valid employee contribution rate.");
            return;
        }

        setPending({ ...pending, employee_contribution_rate: rate });
        pushAIChat("Wage limit?");
        speak("What is the wage limit?");
        return;
    }

    // STEP 6 — WAGE LIMIT
    if (pending.wage_limit === undefined) {
        setPending({ ...pending, wage_limit: Number(text) || 0 });
        pushAIChat("Effective from date? (YYYY-MM-DD)");
        speak("Effective from date?");
        return;
    }

    // STEP 7 — EFFECTIVE FROM
    if (!pending.effective_from) {
        setPending({ ...pending, effective_from: text.trim() });
        pushAIChat("Effective to date? (YYYY-MM-DD)");
        speak("Effective to date?");
        return;
    }

    // STEP 8 — EFFECTIVE TO
    if (!pending.effective_to) {
        setPending({ ...pending, effective_to: text.trim() });
        pushAIChat("Should this configuration be active or inactive?");
        speak("Should this configuration be active or inactive?");
        return;
    }

    // STEP 9 — STATUS (FINAL)
    if (!pending.status) {
        const status = text.toLowerCase().includes("inactive")
            ? "inactive"
            : "active";

        pushAIChat("Creating ESIC configuration...");
        speak("Creating ESIC configuration.");

        window.dispatchEvent(
            new CustomEvent("ai-create-esic-configuration", {
                detail: {
                    ...pending,
                    status
                }
            })
        );

        setPending(null);
    }
};

// -------------------------------
// DELETE ESIC CONFIGURATION — START
// -------------------------------
export const startDeleteEsicConfigurationFlow = (
    setPendingDelete: (data: PendingDeleteEsicConfiguration | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    esicCodeFromIntent?: string
) => {
    if (esicCodeFromIntent) {
        pushAIChat(`Deleting ESIC configuration "${esicCodeFromIntent}"...`);
        speak(`Deleting ESIC configuration ${esicCodeFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-esic-configuration", {
                detail: { esic_code: esicCodeFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingCode: true });
    pushAIChat("Which ESIC code should be deleted?");
    speak("Which ESIC code should be deleted?");
};

// -------------------------------
// DELETE ESIC CONFIGURATION — PROCESS
// -------------------------------
export const processPendingDeleteEsicConfiguration = (
    text: string,
    pending: PendingDeleteEsicConfiguration,
    setPendingDelete: (data: PendingDeleteEsicConfiguration | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingCode) return;

    const esic_code = text.trim();

    if (!esic_code) {
        pushAIChat("Please provide a valid ESIC code.");
        speak("Please provide a valid ESIC code.");
        return;
    }

    pushAIChat(`Deleting ESIC configuration "${esic_code}"...`);
    speak(`Deleting ESIC configuration ${esic_code}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-esic-configuration", {
            detail: { esic_code }
        })
    );

    setPendingDelete(null);
};